import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { ArrowLeft, Save, Send, Loader, Languages, MessageSquare } from 'lucide-react';
import { writingsApi } from '../../api';
import './WritingEditor.css';

const WritingEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('draft');
  const [lastSaved, setLastSaved] = useState(null);
  const [threadResult, setThreadResult] = useState(null);

  useEffect(() => {
    if (id) {
      fetchWriting();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchWriting = async () => {
    try {
      const response = await writingsApi.getById(id);
      setTitle(response.data.title || '');
      setContent(response.data.content || '');
      setStatus(response.data.status || 'draft');
    } catch (error) {
      console.error('Error fetching writing:', error);
      navigate('/writing');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = useCallback(async (newStatus = status) => {
    setSaving(true);
    try {
      await writingsApi.update(id, {
        title: title || 'Untitled',
        content,
        status: newStatus
      });
      setStatus(newStatus);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving writing:', error);
    } finally {
      setSaving(false);
    }
  }, [id, title, content, status]);

  const handlePublish = () => {
    handleSave('published');
  };

  const handleBack = () => {
    navigate('/writing');
  };

  const handleTranslate = async () => {
    if (!content.trim()) return;
    setTranslating(true);
    try {
      const response = await writingsApi.translate(content);
      // Create a new writing with translated content
      const newWriting = await writingsApi.create({
        title: title ? `${title} (EN)` : 'Translated',
        content: response.data.translated,
        status: 'draft'
      });
      // Navigate to the new writing
      navigate(`/writing/${newWriting.data.id}`);
    } catch (error) {
      console.error('Translation error:', error);
      alert('翻译失败: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  const handleSplitThread = async () => {
    if (!content.trim()) return;
    setSplitting(true);
    try {
      const response = await writingsApi.splitThread(content);
      setThreadResult(response.data.thread);
    } catch (error) {
      console.error('Split thread error:', error);
      alert('切分失败: ' + error.message);
    } finally {
      setSplitting(false);
    }
  };

  const copyTweet = (tweet, index) => {
    navigator.clipboard.writeText(tweet);
    // Show brief feedback
    const btn = document.querySelector(`.tweet-item:nth-child(${index + 1}) .copy-btn`);
    if (btn) {
      const originalText = btn.textContent;
      btn.textContent = '已复制!';
      setTimeout(() => { btn.textContent = originalText; }, 1000);
    }
  };

  const copyAllTweets = () => {
    if (!threadResult) return;
    const allText = threadResult.join('\n\n---\n\n');
    navigator.clipboard.writeText(allText);
  };

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    return lastSaved.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="writing-editor-page">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="writing-editor-page">
      <header className="editor-topbar">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={20} />
          <span>返回</span>
        </button>

        <div className="editor-status">
          {lastSaved && (
            <span className="last-saved">已保存于 {formatLastSaved()}</span>
          )}
          <span className={`status-indicator ${status}`}>
            {status === 'published' ? 'Published' : 'Draft'}
          </span>
        </div>

        <div className="editor-actions">
          <button 
            className="ai-btn"
            onClick={handleTranslate}
            disabled={translating || !content.trim()}
            title="翻译成地道英语"
          >
            {translating ? <Loader size={18} className="spin" /> : <Languages size={18} />}
            翻译
          </button>
          <button 
            className="ai-btn"
            onClick={handleSplitThread}
            disabled={splitting || !content.trim()}
            title="切分成CT Thread"
          >
            {splitting ? <Loader size={18} className="spin" /> : <MessageSquare size={18} />}
            Thread
          </button>
          <button 
            className="save-btn"
            onClick={() => handleSave()}
            disabled={saving}
          >
            {saving ? <Loader size={18} className="spin" /> : <Save size={18} />}
            保存
          </button>
          <button 
            className="publish-btn"
            onClick={handlePublish}
            disabled={saving}
          >
            <Send size={18} />
            发布
          </button>
        </div>
      </header>

      <main className="editor-main">
        <div className="editor-container">
          <input
            type="text"
            className="title-input"
            placeholder="标题"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
          <div className="content-editor" data-color-mode="light">
            <MDEditor
              value={content}
              onChange={(val) => setContent(val || '')}
              preview="live"
              height={500}
            />
          </div>
        </div>
      </main>

      {threadResult && (
        <div className="thread-modal-overlay" onClick={() => setThreadResult(null)}>
          <div className="thread-modal" onClick={(e) => e.stopPropagation()}>
            <div className="thread-header">
              <h3>🧵 CT Thread ({threadResult.length} tweets)</h3>
              <div className="thread-header-actions">
                <button className="copy-all-btn" onClick={copyAllTweets}>复制全部</button>
                <button className="close-thread" onClick={() => setThreadResult(null)}>×</button>
              </div>
            </div>
            <div className="thread-list">
              {threadResult.map((tweet, index) => (
                <div key={index} className="tweet-item">
                  <div className="tweet-number">{index + 1}/</div>
                  <div className="tweet-body">
                    <div className="tweet-content">{tweet}</div>
                    <div className="tweet-footer">
                      <span className={`tweet-count ${tweet.length > 280 ? 'over' : ''}`}>{tweet.length}/280</span>
                      <button className="copy-btn" onClick={() => copyTweet(tweet, index)}>
                        复制
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingEditor;
